<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import BottomRightBlob from '@/components/MockExamIntro/BottomRightBlob.vue'
import TopLeftBlob from '@/components/MockExamIntro/TopLeftBlob.vue'
import SelectMockExam from '@/components/MockExamIntro/SelectMockExam.vue'
import MockExamInfo from '@/components/MockExamIntro/MockExamInfo.vue'
import WhatToExpect from '@/components/MockExamIntro/WhatToExpect.vue'
import { userModule } from '@/store/user/module'
import { mockExamModule } from '@/store/mockExam/module'
import { quizModule } from '@/store/quiz/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import type { Study } from '@pocketprep/types'
import type { ClientMockExam } from '@/store/bundle/state'
import { subscriptionModule } from '@/store/subscription/module'
import { upgradeModule } from '@/store/upgrade/module'

type TMockExamIntroStep = 'selectExam' | 'examInfo' | 'whatToExpect'
export type TMappedMockExam = (Study.Class.MockExamJSON | ClientMockExam) & { lastTakenAt: Date | null }

const {
    Icon,
    Button: PocketButton,
    Checkbox,
    Tooltip,
} = UIKit

const router = useRouter()
const route = useRoute()

const isLoading = ref(true)
const step = ref<TMockExamIntroStep>('selectExam')
const selectedMockExamId = ref<string | null>(null)
const getsExtraTime = ref(false)
const isShowingInfoTooltip = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const currentExam = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentExamName = computed(() => {
    return currentExam.value?.nativeAppName || ''
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const mockExamQuizzes = computed(() => {
    const quizzes = quizModule.getters.getQuizzes()
    return quizzes.filter(quiz => quiz.mode === 5).sort((a, b) => {
        // Sort the quizzes by created date so that we'll use the most recent one in mappedMockExams
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
})

const mappedMockExams = computed<TMappedMockExam[]>(() => {
    const mockExams = mockExamModule.getters.getCurrentMockExams()
    return mockExams.map(mockExam => {
        const mockExamQuiz = mockExamQuizzes.value.find(quiz => quiz.mockExam?.objectId === mockExam.objectId)

        return {
            ...mockExam,
            lastTakenAt: mockExamQuiz ? new Date(mockExamQuiz.createdAt) : null,
        }
    })
})

const hasMultipleMockExams = computed(() => {
    return mappedMockExams.value.length > 1
})

const selectedMockExam = computed(() => {
    return mappedMockExams.value.find(mockExam => mockExam.objectId === selectedMockExamId.value) || null
})

onMounted(async () => {
    await mockExamModule.actions.fetchMockExams()

    if (mappedMockExams.value.length === 0) {
        router.push({ name: 'study' })
    } else if (!hasMultipleMockExams.value && mappedMockExams.value[0]?.objectId) {
        selectedMockExamId.value = mappedMockExams.value[0].objectId
        step.value = 'examInfo'
    }

    focusTitle()
    isLoading.value = false
})

const focusTitle = () => {
    setTimeout(() => {
        const titleEl = document.querySelector<HTMLElement>('.mock-exam-intro__title')
        titleEl?.focus()
    }, 0)
}

watch(step, () => {
    focusTitle()
})

const backToStudy = () => {
    router.push({
        name: 'study',
    })
}

const selectMockExam = (id: string) => {
    selectedMockExamId.value = selectedMockExamId.value === id ? null : id
}

const previousStep = () => {
    if (step.value === 'selectExam') {
        backToStudy()
    } else if (step.value === 'examInfo' && !hasMultipleMockExams.value) {
        backToStudy()
    } else if (step.value === 'examInfo' && hasMultipleMockExams.value) {
        step.value = 'selectExam'
    } else if (step.value === 'whatToExpect') {
        step.value = 'examInfo'
    }
}

const nextStep = async () => {
    if (step.value === 'selectExam') {
        step.value = 'examInfo'
    } else if (step.value === 'examInfo') {
        step.value = 'whatToExpect'
    } else if (step.value === 'whatToExpect') {
        if (!hasActiveSubscription.value) {
            upgradeModule.actions.openUpgradeModal()
        } else if (selectedMockExamId.value) {
            isLoading.value = true
            await mockExamModule.actions.createActiveMockExamQuiz({
                mockExamId: selectedMockExamId.value,
                getsExtraTime: getsExtraTime.value,
            })
            router.push({
                name: 'mock-exam',
                state: { fromName: typeof route.name === 'string' ? route.name : 'mock-exam-intro' },
            })
        }
    }
}

const toggleGetsExtraTime = () => {
    getsExtraTime.value = !getsExtraTime.value
}

const showInfoTooltip = () => {
    isShowingInfoTooltip.value = true
}

const hideInfoTooltip = () => {
    isShowingInfoTooltip.value = false
}
</script>

<template>
    <div class="mock-exam-intro__container">
        <div
            id="mock-exam-intro"
            v-dark
            class="mock-exam-intro"
        >
            <Icon
                v-dark
                type="close"
                class="mock-exam-intro__close-icon"
                title="Close"
                tabindex="0"
                @click="backToStudy"
                @keydown.enter="backToStudy"
                @mousedown.prevent
            />
            <BottomRightBlob class="mock-exam-intro__bottom-right-blob" />
            <TopLeftBlob class="mock-exam-intro__top-left-blob" />
            <div v-dark class="mock-exam-intro__header">
                <div
                    tabindex="-1"
                    class="mock-exam-intro__title"
                    role="header"
                >
                    <Icon
                        v-dark
                        type="exam"
                        class="mock-exam-intro__title-icon"
                    />
                    Pocket Prep Mock Exam
                    <div
                        class="mock-exam-intro__a11y-step"
                        :style="{
                            width: '0',
                            height: '0',
                            overflow: 'hidden',
                            display: 'inline-flex',
                        }"
                    >
                        {{ step }}
                    </div>
                </div>
                <div v-dark class="mock-exam-intro__exam-name">
                    {{ currentExamName }}
                </div>
            </div>
            <div v-dark class="mock-exam-intro__content">
                <template v-if="isLoading">
                    <Icon
                        v-if="isLoading"
                        class="mock-exam-intro__loading"
                        type="loading2"
                        :is-dark-mode="isDarkMode"
                    />
                </template>
                <template v-else-if="step === 'selectExam'">
                    <SelectMockExam
                        :mock-exams="mappedMockExams"
                        :current-exam-name="currentExamName"
                        :selected-mock-exam-id="selectedMockExamId"
                        @selectMockExam="selectMockExam"
                    />
                </template>
                <template v-else-if="selectedMockExam && step === 'examInfo'">
                    <MockExamInfo
                        :mock-exam="selectedMockExam"
                        :current-exam-name="currentExamName"
                    >
                        <template #extraTime>
                            <div
                                class="mock-exam-intro__extra-time"
                                @click="toggleGetsExtraTime"
                            >
                                <Checkbox
                                    class="mock-exam-intro__extra-time-checkbox"
                                    aria-label="I will get extra time during my test for accessibility reasons"
                                    :modelValue="getsExtraTime"
                                    :is-dark-mode="isDarkMode"
                                    @keydown.enter.space="toggleGetsExtraTime"
                                />
                                <span
                                    v-dark
                                    class="mock-exam-intro__extra-time-label"
                                    :class="{
                                        'mock-exam-intro__extra-time-label--selected': getsExtraTime,
                                    }"
                                >
                                    I will get extra time during my test for accessibility reasons
                                </span>
                                <span
                                    v-dark
                                    class="mock-exam-intro__extra-time-info"
                                    tabindex="0"
                                    aria-label="If selected, timer will count up after reaching 0:00"
                                    @focus="showInfoTooltip"
                                    @blur="hideInfoTooltip"
                                    @mouseenter="showInfoTooltip"
                                    @mouseleave="hideInfoTooltip"
                                >
                                    <Tooltip
                                        v-if="isShowingInfoTooltip"
                                        theme="leftalign"
                                        :is-dark-mode="isDarkMode"
                                    >
                                        If selected, timer will count up after reaching 0:00
                                    </Tooltip>
                                    <Icon
                                        v-dark
                                        type="info"
                                        class="mock-exam-intro__extra-time-info-icon"
                                    />
                                </span>
                            </div>
                        </template>
                    </MockExamInfo>
                </template>
                <template v-else-if="selectedMockExam && step === 'whatToExpect'">
                    <WhatToExpect />
                </template>
            </div>
            <div v-dark class="mock-exam-intro__footer">
                <PocketButton
                    type="secondary"
                    :is-dark-mode="isDarkMode"
                    @click="previousStep"
                >
                    {{
                        (step === 'selectExam' || (step === 'examInfo' && !hasMultipleMockExams))
                            ? 'Cancel'
                            : 'Back'
                    }}
                </PocketButton>
                <PocketButton
                    class="mock-exam-intro__next-btn"
                    :is-dark-mode="isDarkMode"
                    :disabled="!selectedMockExamId"
                    :is-loading="isLoading"
                    @click="nextStep"
                >
                    {{
                        step === 'selectExam'
                            ? 'Exam Information'
                            : step === 'examInfo'
                                ? 'What to Expect'
                                : hasActiveSubscription
                                    ? 'Start Exam'
                                    : 'Upgrade to Premium'
                    }}<Icon
                        type="arrow"
                        class="mock-exam-intro__next-btn-arrow"
                    />
                </PocketButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.mock-exam-intro {
    position: relative;
    background-color: $gray-background;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100%;

    &--dark {
        background-color: $charcoal;
    }

    &__container {
        height: calc(100% - 87px);
        overflow: auto; // overflow:auto and position:relative need to be on different elements

        // Including breakpoint for mobile footer height calculation
        @include breakpoint(black-bear) {
            height: calc(100% - 68px);
        }
    }

    &__close-icon {
        position: absolute;
        width: 30px;
        height: 30px;
        top: 29px;
        right: 28px;
        color: $brand-blue;
        cursor: pointer;
        outline: none;

        &:hover {
            color: $brand-black;
        }

        &:focus {
            content: '';
            position: absolute;
            border: 1px solid $brand-blue;
            border-radius: 5px;
            top: 28px;
            right: 27px;
        }

        &--dark {
            color: $banana-bread;

            &:hover {
                color: $butterscotch;
            }
        }

        @include breakpoint(black-bear) {
            top: 14px;
            right: 15px;

            &:focus {
                top: 13px;
                right: 14px;
            }
        }
    }

    &__bottom-right-blob {
        position: absolute;
        bottom: 0;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__top-left-blob {
        position: absolute;
        top: 0;
        left: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__header {
        margin-top: 103px;
        width: 460px;

        &--dark {
            color: $white;
        }

        @include breakpoint(black-bear) {
            margin-top: 105px;
            width: 308px;
        }
    }

    &__title {
        outline: none;
        position: relative;
        font-weight: 600;
        font-size: 26px;
        line-height: 34px;
        margin-bottom: 2px;
    }

    &__title-icon {
        position: absolute;
        width: 24px;
        height: 24px;
        top: 4px;
        left: -39px;

        &--dark {
            color: $fog;
        }

        @include breakpoint(black-bear) {
            top: -30px;
            left: -3px;
        }
    }

    &__exam-name {
        color: $ash;
        font-weight: 600;
        font-size: 14px;
        letter-spacing: 0.2px;
        line-height: 17px;
        margin-bottom: 26px;

        &--dark {
            color: $fog;
        }

        @include breakpoint(black-bear) {
            margin-bottom: 22px;
        }
    }

    &__content {
        z-index: 1;

        &--dark {
            color: $white;
        }
    }

    &__loading {
        position: absolute;
        top: 30%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    &__extra-time {
        display: flex;
        align-items: center;
        height: 45px;
        padding-left: 21px;

        &:hover {
            cursor: pointer;
        }

        @include breakpoint(black-bear) {
            height: 58px;
            padding-left: 15px;
        }
    }

    &__extra-time-checkbox {
        width: 15px;
        height: 15px;
        min-width: 15px;
        min-height: 15px;
        margin-right: 12px;

        @include breakpoint(black-bear) {
            position: relative;
            top: -10px;
        }
    }

    &__extra-time-label {
        color: $ash;
        font-size: 15px;
        line-height: 18px;
        margin-right: 4px;

        &--dark {
            color: rgba($white, 0.72);
        }

        &--selected {
            color: $brand-black;

            &--dark {
                color: $white;
            }
        }
    }

    &__extra-time-info {
        position: relative;
        top: 2px;
        color: $brand-blue;
        outline: none;

        @media (hover: hover) {
            &:hover {
                color: $brand-black;
            }
        }

        &:focus {
            color: $brand-black;
        }

        &--dark {
            color: $banana-bread;

            @media (hover: hover) {
                &:hover {
                    color: $butterscotch;
                }
            }

            &:focus {
                color: $butterscotch;
            }
        }

        :deep(.uikit-tooltip) {
            position: absolute;
            left: 50%;
            top: calc(-100% - 14px);
        }

        @include breakpoint(black-bear) {
            :deep(.uikit-tooltip) {
                left: calc(50% - 7px);
                top: calc(-100% - 6px);
            }
        }
    }

    &__extra-time-info-icon {
        @include breakpoint(black-bear) {
            width: 24px;
            height: 24px;
            margin-right: 13px;
        }
    }

    &__footer {
        position: fixed;
        bottom: 0;
        width: 100%;
        height: 87px;
        background-color: $white;
        display: flex;
        justify-content: center;
        align-items: center;
        border-top: 1px solid $fog;
        z-index: 2;

        &--dark {
            background-color: $mariner;
            border-color: $brand-black;
        }

        @include breakpoint(black-bear) {
            height: 68px;
        }
    }

    &__next-btn {
        margin-left: 8px;
        padding-left: 14px;
        padding-right: 12px;
    }

    &__next-btn-arrow {
        margin-left: 10px;
    }
}
</style>
